import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import {
  EnvelopeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { toast } from "react-toastify";

function ForgotPassword() {
  const navigate = useNavigate();
  const otpInputRef = useRef(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [step, setStep] = useState("email"); // email, otp, newPassword
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: "",
    color: "bg-gray-200",
  });

  const validatePassword = (pwd) => {
    const requirements = {
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /\d/.test(pwd),
      special: /[!@#$%^&*()_]/.test(pwd),
    };
    const score = Object.values(requirements).filter(Boolean).length;
    let feedback = "";
    let color = "bg-gray-200";
    if (score === 0) {
      feedback = "Enter a password";
      color = "bg-gray-200";
    } else if (score === 1) {
      feedback = "Very weak";
      color = "bg-red-500";
    } else if (score === 2) {
      feedback = "Weak";
      color = "bg-orange-500";
    } else if (score === 3) {
      feedback = "Fair";
      color = "bg-yellow-500";
    } else if (score === 4) {
      feedback = "Good";
      color = "bg-green-500";
    } else if (score === 5) {
      feedback = "Strong";
      color = "bg-green-700";
    }
    return { score, feedback, color, requirements };
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return emailRegex.test(email);
  };

  const passwordsMatch = () => {
    return newPassword && confirmPassword && newPassword === confirmPassword;
  };

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setNewPassword(pwd);
    setPasswordStrength(validatePassword(pwd));
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setError("");
  };

  const handleSendResetLink = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!email.trim()) {
      setError("Email is required");
      toast.error("Email is required");
      return;
    }
    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      toast.error("Please enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess("Password reset link has been sent to your email. Please check your inbox.");
        setStep("otp");
      } else {
        const msg = data.message || "Failed to send reset link. Please try again.";
        setError(msg);
        toast.error(msg);
      }
    } catch (err) {
      console.error("Forgot password error:", err);
      setError("Server error. Please check your connection and try again.");
      toast.error("Server error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!otp.trim()) {
      setError("OTP is required");
      toast.error("OTP is required");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          otp: otp.trim(),
        }),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess("OTP verified successfully. Please set your new password.");
        setStep("newPassword");
      } else {
        const msg = data.message || "Invalid OTP. Please try again.";
        setError(msg);
        toast.error(msg);
      }
    } catch (err) {
      console.error("OTP verification error:", err);
      setError("Server error. Please check your connection and try again.");
      toast.error("Server error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  // UPDATED RESEND OTP FUNCTION
  const handleResendOTP = async () => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess("A new OTP has been sent to your email.");
      } else {
        const msg = data.message || "Failed to resend OTP.";
        setError(msg);
        toast.error(msg);
      }
    } catch (err) {
      setError("Failed to resend OTP. Please try again.");
      toast.error("Failed to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!newPassword) {
      setError("New password is required");
      toast.error("New password is required");
      return;
    }
    if (passwordStrength.score < 4) {
      setError(
        "Password must meet strength requirements (min 8 chars, 1 uppercase, 1 number, 1 special char)"
      );
      toast.error("Password must meet strength requirements (min 8 chars, 1 uppercase, 1 number, 1 special char)");
      return;
    }
    if (!confirmPassword) {
      setError("Please confirm your password");
      toast.error("Please confirm your password");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          otp: otp.trim(),
          new_password: newPassword,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess("Password reset successfully! You can now log in with your new password.");
        setEmail("");
        setOtp("");
        setNewPassword("");
        setConfirmPassword("");
        setPasswordStrength({ score: 0, feedback: "", color: "bg-gray-200" });
        setStep("email");
        navigate("/");
      } else {
        const msg = data.message || "Failed to reset password. Please try again.";
        setError(msg);
        toast.error(msg);
      }
    } catch (err) {
      console.error("Password reset error:", err);
      setError("Server error. Please check your connection and try again.");
      toast.error("Server error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case "email":
        return "Reset password";
      case "otp":
        return "Enter verification code";
      case "newPassword":
        return "Set new password";
      default:
        return "Reset password";
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case "email":
        return "Enter your email address and we'll send you a reset code.";
      case "otp":
        return "Enter the 6-digit code sent to your email.";
      case "newPassword":
        return "Create a new password for your account.";
      default:
        return "Enter your email address and we'll send you a reset code.";
    }
  };

  const FRAUNCES = "'Fraunces', Georgia, serif";

  // Strength bar tone: coral when good/strong, slate when fair, danger when weak
  const strengthBarColor = (i) => {
    if (passwordStrength.score < i) return 'var(--an-hair)';
    if (passwordStrength.score >= 4) return 'var(--an-coral)';
    if (passwordStrength.score >= 2) return 'var(--an-slate)';
    return 'var(--an-danger)';
  };
  const strengthLabelColor =
    passwordStrength.score >= 4
      ? 'var(--an-coral-deep)'
      : passwordStrength.score >= 2
      ? 'var(--an-slate)'
      : 'var(--an-danger)';

  return (
    <div className="min-h-screen w-full" style={{ background: 'var(--an-white)' }}>
      <style>{`
        .an-login-input:focus-within { box-shadow: 0 0 0 2px rgba(255,122,102,.45); }
        .an-login-cta { transition: transform .2s, box-shadow .2s; }
        .an-login-cta:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 12px 28px -12px rgba(255,122,102,.8); }
        .an-login-ghost { transition: background-color .15s, color .15s; }
        .an-login-ghost:hover { background: var(--an-ink); color: #fff; }
        .an-login-back:hover { background: rgba(20,20,30,.04); }
        .an-login-input input:-webkit-autofill,
        .an-login-input input:-webkit-autofill:hover,
        .an-login-input input:-webkit-autofill:focus,
        .an-login-input input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px var(--an-mist) inset !important;
          box-shadow: 0 0 0 1000px var(--an-mist) inset !important;
          -webkit-text-fill-color: var(--an-ink) !important;
          caret-color: var(--an-ink) !important;
          transition: background-color 9999s ease-in-out 0s;
        }
      `}</style>

      <div className="min-h-screen flex flex-col lg:flex-row lg:max-w-[1400px] mx-auto">

        {/* ─── LEFT — dark brand panel ─────────────────────────────────── */}
        <div
          className="hidden lg:flex lg:flex-1 lg:basis-1/2 relative overflow-hidden flex-col justify-between p-10 xl:p-14 text-white min-h-screen"
          style={{ background: 'var(--an-ink)' }}
        >
          {/* Wordmark — anab + lift-off-l + r */}
          <div
            className="relative z-10 flex items-baseline text-3xl xl:text-4xl font-semibold"
            style={{ letterSpacing: '-0.02em' }}
          >
            anab
            <svg className="inline-block h-7 xl:h-8 w-auto -mb-0.5 mx-0.5" viewBox="0 0 34 100" aria-hidden="true">
              <path d="M10 93 L22 24 L30 12" fill="none" stroke="var(--an-coral)" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            r
          </div>

          {/* Pitch — Fraunces display */}
          <div className="relative z-10">
            <div
              style={{
                fontFamily: FRAUNCES,
                fontWeight: 600,
                fontSize: 54,
                letterSpacing: '-0.015em',
                lineHeight: 1.02,
                textWrap: 'balance',
                maxWidth: 440,
              }}
            >
              The <span style={{ color: 'var(--an-coral)' }}>OS</span> for the startup ecosystem.
            </div>
            <div
              style={{
                color: 'var(--an-silver)',
                fontSize: 16,
                marginTop: 22,
                lineHeight: 1.6,
                maxWidth: '30rem',
              }}
            >
              Run startups, mentors, sessions, events, grants, and reporting from one place. Built for incubators, accelerators, and venture studios.
            </div>
          </div>

          {/* Footer */}
          <div className="relative z-10" style={{ fontSize: 12, color: '#83838E' }}>
            © 2026 anablr · Incubator Co-pilot
          </div>

          {/* Decorative big l in the corner */}
          <svg className="absolute -right-12 -bottom-20 w-[320px] h-[440px]" viewBox="0 0 34 100" aria-hidden="true" style={{ opacity: 0.14 }}>
            <path d="M10 93 L22 24 L30 12" fill="none" stroke="var(--an-coral)" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {/* Mobile brand strip */}
        <div
          className="lg:hidden flex items-baseline justify-center text-2xl font-semibold tracking-tight text-white px-6 py-4"
          style={{ background: 'var(--an-ink)' }}
        >
          anab
          <svg className="inline-block h-5 w-auto mx-0.5 -mb-0.5" viewBox="0 0 34 100" aria-hidden="true">
            <path d="M10 93 L22 24 L30 12" fill="none" stroke="var(--an-coral)" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          r
        </div>

        {/* ─── RIGHT — white form panel ────────────────────────────────── */}
        <div
          className="lg:flex-1 lg:basis-1/2 flex flex-col items-center justify-center p-6 sm:p-10 lg:p-12 flex-1 min-h-screen"
          style={{ background: 'var(--an-white)' }}
        >
          <div className="w-full max-w-[420px]">
            {/* Headline — Fraunces display */}
            <h2
              style={{
                fontFamily: FRAUNCES,
                fontWeight: 600,
                fontSize: 38,
                letterSpacing: '-0.01em',
                lineHeight: 1.1,
                color: 'var(--an-ink)',
                marginBottom: 8,
              }}
            >
              {getStepTitle()}
            </h2>
            <p style={{ fontSize: 15, color: 'var(--an-slate)', marginBottom: 36 }}>
              {getStepDescription()}
            </p>

          {/* Steps */}
          <div>
            {/* EMAIL STEP */}
            {step === 'email' && (
              <form onSubmit={handleSendResetLink} className="space-y-4">
                <div>
                  <label
                    htmlFor="email"
                    className="block"
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: 'var(--an-slate)',
                      marginBottom: 9,
                    }}
                  >
                    Work email
                  </label>
                  <div
                    className="an-login-input flex items-center"
                    style={{
                      gap: 11,
                      background: 'var(--an-mist)',
                      borderRadius: 12,
                      padding: '14px 15px',
                      border: error ? '1px solid var(--an-danger)' : 'none',
                    }}
                  >
                    <EnvelopeIcon className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--an-slate)' }} />
                    <input
                      type="email"
                      id="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={handleEmailChange}
                      className="w-full bg-transparent border-0 outline-none"
                      style={{ color: 'var(--an-ink)', fontSize: 15 }}
                      required
                    />
                  </div>
                </div>

                {error && (
                  <div
                    role="alert"
                    style={{
                      background: 'rgba(196,69,60,.06)',
                      border: '1px solid rgba(196,69,60,.35)',
                      color: 'var(--an-danger)',
                      borderRadius: 12,
                      padding: '12px 14px',
                      fontSize: 14,
                    }}
                  >
                    {error}
                  </div>
                )}
                {success && (
                  <div
                    role="status"
                    style={{
                      background: 'rgba(255,122,102,.08)',
                      border: '1px solid rgba(255,122,102,.35)',
                      color: 'var(--an-coral-deep)',
                      borderRadius: 12,
                      padding: '12px 14px',
                      fontSize: 14,
                    }}
                  >
                    {success}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="an-login-cta w-full disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none"
                  style={{
                    padding: 15,
                    background: 'var(--an-coral)',
                    color: 'var(--an-on-coral)',
                    border: 'none',
                    borderRadius: 12,
                    fontWeight: 600,
                    fontSize: 15,
                    boxShadow: '0 10px 24px -10px rgba(255,122,102,.7)',
                  }}
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2" style={{ borderColor: 'var(--an-on-coral)' }} />
                      <span>Sending reset code...</span>
                    </div>
                  ) : (
                    'Send reset code'
                  )}
                </button>
              </form>
            )}

            {/* OTP STEP */}
            {step === 'otp' && (
              <form onSubmit={handleVerifyOTP} className="space-y-4">
                <div>
                  <label
                    htmlFor="otp"
                    className="block"
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: 'var(--an-slate)',
                      marginBottom: 9,
                    }}
                  >
                    Verification code
                  </label>
                  <div className="relative">
                    {/* Hidden real input that captures typing */}
                    <input
                      ref={otpInputRef}
                      type="text"
                      id="otp"
                      placeholder="Enter OTP from your email"
                      value={otp}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setOtp(v);
                      }}
                      className="absolute inset-0 w-full h-12 opacity-0 focus:opacity-0"
                      required
                      maxLength={6}
                      inputMode="numeric"
                      pattern="\d*"
                      autoComplete="one-time-code"
                    />
                    {/* 6 visual mist tiles reflecting the OTP value */}
                    <div className="grid grid-cols-6 gap-2" onClick={() => otpInputRef.current?.focus()}>
                      {[0,1,2,3,4,5].map((i) => (
                        <div
                          key={i}
                          className="h-12 flex items-center justify-center"
                          style={{
                            background: 'var(--an-mist)',
                            borderRadius: 10,
                            color: 'var(--an-ink)',
                            fontWeight: 600,
                          }}
                        >
                          {otp?.[i] ? (
                            <span style={{ fontSize: 18 }}>{otp[i]}</span>
                          ) : i === Math.min(otp?.length || 0, 5) ? (
                            <span
                              className="animate-pulse"
                              style={{
                                display: 'inline-block',
                                width: 2,
                                height: 20,
                                background: 'var(--an-slate)',
                                borderRadius: 2,
                              }}
                            />
                          ) : (
                            <span style={{ fontSize: 18, color: 'transparent' }}>0</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {error && (
                  <div
                    role="alert"
                    style={{
                      background: 'rgba(196,69,60,.06)',
                      border: '1px solid rgba(196,69,60,.35)',
                      color: 'var(--an-danger)',
                      borderRadius: 12,
                      padding: '12px 14px',
                      fontSize: 14,
                    }}
                  >
                    {error}
                  </div>
                )}
                {success && (
                  <div
                    role="status"
                    style={{
                      background: 'rgba(255,122,102,.08)',
                      border: '1px solid rgba(255,122,102,.35)',
                      color: 'var(--an-coral-deep)',
                      borderRadius: 12,
                      padding: '12px 14px',
                      fontSize: 14,
                    }}
                  >
                    {success}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="an-login-cta w-full disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none"
                  style={{
                    padding: 15,
                    background: 'var(--an-coral)',
                    color: 'var(--an-on-coral)',
                    border: 'none',
                    borderRadius: 12,
                    fontWeight: 600,
                    fontSize: 15,
                    boxShadow: '0 10px 24px -10px rgba(255,122,102,.7)',
                  }}
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2" style={{ borderColor: 'var(--an-on-coral)' }} />
                      <span>Verifying...</span>
                    </div>
                  ) : (
                    'Verify'
                  )}
                </button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={loading}
                    className="underline hover:no-underline transition-colors disabled:opacity-50"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--an-slate)',
                      fontSize: 14,
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => { if (!loading) e.currentTarget.style.color = 'var(--an-coral-deep)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--an-slate)'; }}
                  >
                    Didn't receive the code? Resend
                  </button>
                </div>
              </form>
            )}

            {/* NEW PASSWORD STEP */}
            {step === 'newPassword' && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label
                    htmlFor="newPassword"
                    className="block"
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: 'var(--an-slate)',
                      marginBottom: 9,
                    }}
                  >
                    New password
                  </label>
                  <div
                    className="an-login-input flex items-center"
                    style={{
                      gap: 11,
                      background: 'var(--an-mist)',
                      borderRadius: 12,
                      padding: '14px 15px',
                    }}
                  >
                    <LockClosedIcon className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--an-slate)' }} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="newPassword"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={handlePasswordChange}
                      className="w-full bg-transparent border-0 outline-none"
                      style={{ color: 'var(--an-ink)', fontSize: 15 }}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="flex-shrink-0 hover:opacity-80 focus:outline-none"
                      style={{ background: 'transparent', border: 'none', color: 'var(--an-slate)', cursor: 'pointer' }}
                    >
                      {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block"
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: 'var(--an-slate)',
                      marginBottom: 9,
                    }}
                  >
                    Confirm password
                  </label>
                  <div
                    className="an-login-input flex items-center"
                    style={{
                      gap: 11,
                      background: 'var(--an-mist)',
                      borderRadius: 12,
                      padding: '14px 15px',
                      border: confirmPassword && newPassword && !passwordsMatch() ? '1px solid var(--an-danger)' : 'none',
                    }}
                  >
                    <CheckCircleIcon className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--an-slate)' }} />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full bg-transparent border-0 outline-none"
                      style={{ color: 'var(--an-ink)', fontSize: 15 }}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      className="flex-shrink-0 hover:opacity-80 focus:outline-none"
                      style={{ background: 'transparent', border: 'none', color: 'var(--an-slate)', cursor: 'pointer' }}
                    >
                      {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                {confirmPassword && newPassword && (
                  <div className="mt-1">
                    <div
                      className="flex items-center justify-center gap-2"
                      style={{
                        padding: '6px 10px',
                        borderRadius: 8,
                        fontSize: 12,
                        fontWeight: 500,
                        background: passwordsMatch() ? 'rgba(255,122,102,.08)' : 'rgba(196,69,60,.06)',
                        border: `1px solid ${passwordsMatch() ? 'rgba(255,122,102,.35)' : 'rgba(196,69,60,.35)'}`,
                        color: passwordsMatch() ? 'var(--an-coral-deep)' : 'var(--an-danger)',
                      }}
                    >
                      <CheckCircleIcon className="h-4 w-4" />
                      <span>{passwordsMatch() ? 'Passwords match' : 'Passwords do not match'}</span>
                    </div>
                  </div>
                )}
                {newPassword && (
                  <div
                    style={{
                      background: 'var(--an-mist)',
                      border: '1px solid var(--an-hair)',
                      borderRadius: 12,
                      padding: 14,
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span style={{ color: 'var(--an-ink)', fontSize: 12, fontWeight: 600 }}>Password strength</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: strengthLabelColor }}>{passwordStrength.feedback}</span>
                    </div>
                    <div className="flex gap-1 mb-2">
                      {[1,2,3,4,5].map((i) => (
                        <div
                          key={i}
                          style={{ flex: 1, height: 4, borderRadius: 2, background: strengthBarColor(i) }}
                        />
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-1.5" style={{ fontSize: 11 }}>
                      <div className="flex items-center gap-2" style={{ color: newPassword.length >= 8 ? 'var(--an-coral-deep)' : 'var(--an-silver)' }}>
                        <CheckCircleIcon className="h-4 w-4" />
                        <span>8+ characters</span>
                      </div>
                      <div className="flex items-center gap-2" style={{ color: /[A-Z]/.test(newPassword) ? 'var(--an-coral-deep)' : 'var(--an-silver)' }}>
                        <CheckCircleIcon className="h-4 w-4" />
                        <span>Uppercase</span>
                      </div>
                      <div className="flex items-center gap-2" style={{ color: /[a-z]/.test(newPassword) ? 'var(--an-coral-deep)' : 'var(--an-silver)' }}>
                        <CheckCircleIcon className="h-4 w-4" />
                        <span>Lowercase</span>
                      </div>
                      <div className="flex items-center gap-2" style={{ color: /\d/.test(newPassword) ? 'var(--an-coral-deep)' : 'var(--an-silver)' }}>
                        <CheckCircleIcon className="h-4 w-4" />
                        <span>Number</span>
                      </div>
                      <div className="flex items-center gap-2 col-span-2" style={{ color: /[!@#$%^&*()_]/.test(newPassword) ? 'var(--an-coral-deep)' : 'var(--an-silver)' }}>
                        <CheckCircleIcon className="h-4 w-4" />
                        <span>Special character (~!@#$%^&amp;*())</span>
                      </div>
                    </div>
                  </div>
                )}
                {error && (
                  <div
                    role="alert"
                    style={{
                      background: 'rgba(196,69,60,.06)',
                      border: '1px solid rgba(196,69,60,.35)',
                      color: 'var(--an-danger)',
                      borderRadius: 12,
                      padding: '12px 14px',
                      fontSize: 14,
                    }}
                  >
                    {error}
                  </div>
                )}
                {success && (
                  <div
                    role="status"
                    style={{
                      background: 'rgba(255,122,102,.08)',
                      border: '1px solid rgba(255,122,102,.35)',
                      color: 'var(--an-coral-deep)',
                      borderRadius: 12,
                      padding: '12px 14px',
                      fontSize: 14,
                    }}
                  >
                    {success}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading || passwordStrength.score < 4 || !passwordsMatch()}
                  className="an-login-cta w-full disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none"
                  style={{
                    padding: 15,
                    background: 'var(--an-coral)',
                    color: 'var(--an-on-coral)',
                    border: 'none',
                    borderRadius: 12,
                    fontWeight: 600,
                    fontSize: 15,
                    boxShadow: '0 10px 24px -10px rgba(255,122,102,.7)',
                  }}
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2" style={{ borderColor: 'var(--an-on-coral)' }} />
                      <span>Resetting password...</span>
                    </div>
                  ) : (
                    'Reset password'
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Divider + Back to Sign in */}
          <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full" style={{ borderTop: '1px solid var(--an-hair)' }} />
              </div>
              <div className="relative flex justify-center text-sm">
                <span style={{ padding: '0 16px', color: 'var(--an-slate)', background: 'var(--an-white)', fontSize: 13 }}>
                  Remember your password?
                </span>
              </div>
            </div>
            <div className="mt-4">
              <Link
                to="/"
                className="an-login-back w-full inline-flex justify-center items-center gap-2 focus:outline-none"
                style={{
                  padding: 14,
                  border: '1.5px solid var(--an-hair)',
                  borderRadius: 12,
                  background: 'transparent',
                  color: 'var(--an-ink)',
                  fontWeight: 600,
                  fontSize: 15,
                  textDecoration: 'none',
                }}
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back to sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
